import { useMemo, useEffect } from "react";
import { useCheckoutSend, useCheckoutStateSelector } from "@checkout/context";
import { useI18nContext } from "@hopper-b2b/i18n";
import type { FiatPrice } from "@hopper-b2b/types";
import {
  useConvertCashToPointsQuery,
  setRewardsBalance,
  type RewardsPaymentEvents,
  usePointsBalance,
} from "@hsbc/rewards";
import { getAfterQuoteTotalPrice } from "@hsbc/shared";
import { roundToTwoDecimal } from "@hsbc/utilities";
import { getWalletCreditPayAmount } from "@hsbc/wallet";

export interface UseAccountPointsReturn {
  maxToPayInCash: FiatPrice;
  maxToPayInPoints: number;
  isToPointLoading: boolean;
  isBalanceLoading: boolean;
  accountBalanceInPoints: number | undefined;
  accountBalanceInCash: FiatPrice;
  oneDollarInPoints: number;
}

export const useAccountPoints = (): UseAccountPointsReturn => {
  const { brand } = useI18nContext();
  const send = useCheckoutSend<RewardsPaymentEvents>();

  const zeroFiat: FiatPrice = {
    value: 0,
    currencyCode: brand.currency.code,
    currencySymbol: brand.currency.symbol ?? "",
  };

  const { fiat: totalPriceCash } = useCheckoutStateSelector(
    getAfterQuoteTotalPrice
  );
  const creditPayCash = useCheckoutStateSelector(getWalletCreditPayAmount);

  const {
    rewardsPoints: rewardsPointsData,
    cashEquivalent: accountBalanceInCash = zeroFiat,
    conversionMultiplier,
    isLoading: isBalanceLoading,
  } = usePointsBalance(true);

  const oneDollarInPoints = useMemo(
    () =>
      conversionMultiplier !== undefined
        ? Math.round(1 / conversionMultiplier)
        : 0,
    [conversionMultiplier]
  );

  const maxToPayInCash = useMemo(
    () => ({
      ...totalPriceCash,
      // Clamp to 0: the applied travel credit can transiently exceed the total
      // (e.g. before the quote total is known), and a negative cash amount is
      // rejected by the rewards conversion endpoint.
      value: Math.max(
        Math.min(
          roundToTwoDecimal(
            totalPriceCash?.value - (creditPayCash?.value ?? 0)
          ),
          accountBalanceInCash?.value
        ),
        0
      ),
    }),
    [totalPriceCash, creditPayCash?.value, accountBalanceInCash?.value]
  );

  const { data: maxToPayInPointsData, isFetching: isToPointLoading } =
    useConvertCashToPointsQuery(maxToPayInCash);

  // Inputs important here as can cause infinite loop
  useEffect(() => {
    if (isBalanceLoading || rewardsPointsData == null) {
      return;
    }

    send(setRewardsBalance(rewardsPointsData.value, accountBalanceInCash));
  }, [accountBalanceInCash, isBalanceLoading, rewardsPointsData, send]);

  return useMemo(
    () => ({
      maxToPayInCash,
      oneDollarInPoints,
      isToPointLoading,
      maxToPayInPoints: maxToPayInPointsData?.numberOfPoints ?? 0,
      isBalanceLoading,
      accountBalanceInCash,
      accountBalanceInPoints: rewardsPointsData?.value ?? 0,
    }),
    [
      accountBalanceInCash,
      isBalanceLoading,
      isToPointLoading,
      maxToPayInCash,
      maxToPayInPointsData?.numberOfPoints,
      oneDollarInPoints,
      rewardsPointsData?.value,
    ]
  );
};
